import { createTheme, Theme } from '@mui/material/styles';

// Coinbase Design System Colors (extracted from Figma design tokens)
const coinbaseColors = {
  // Primary brand colors from design tokens
  primary: '#2752E7',      // Main Coinbase Blue
  primaryAccent: '#273B4A', // Dark Blue Accent
  secondary: '#1A82FF',    // Light blue variant
  
  // Base colors
  white: '#FFFFFF',
  black: '#000000',
  darkGray: '#111111',
  mediumGray: '#2C3131',
  lightGray: '#707070',
  borderGray: '#E9E9E9',
  subtleGray: '#CFCFCF',
  textGray: '#A9A9A9',
  
  // Background colors
  bgPrimary: '#F5F8FE',    // Light blue background
  bgSecondary: '#FCFCFE',  // Almost white
  bgDark: '#24262B',
  
  // Accent colors
  orange: '#FFBE55',
  green: '#43A047',
  red: '#FF0000',
  warning: '#FF9900',
  
  // Blue variations
  blueLight: '#1A82FF',
  blueDark: '#164B79',
  blueAccent: '#0A84FF',
  
  // Dark mode alternatives
  dark: {
    background: '#24262B',   // Dark background
    surface: '#2C3131',      // Dark gray - cards
    text: '#FFFFFF',         // White - primary text
    muted: '#A9A9A9',        // Light gray - secondary text
  }
};

export const createAppTheme = (mode: 'light' | 'dark'): Theme => {
  return createTheme({
    palette: {
      mode,
      primary: {
        main: coinbaseColors.primary,
        dark: coinbaseColors.blueDark,
        light: coinbaseColors.blueLight,
      },
      secondary: {
        main: coinbaseColors.secondary,
        dark: coinbaseColors.primaryAccent,
      },
      success: {
        main: coinbaseColors.green,
      },
      warning: {
        main: coinbaseColors.warning,
      },
      error: {
        main: coinbaseColors.red,
      },
      text: {
        primary: mode === 'dark' ? coinbaseColors.dark.text : coinbaseColors.darkGray,
        secondary: mode === 'dark' ? coinbaseColors.dark.muted : coinbaseColors.lightGray,
      },
      background: {
        default: mode === 'dark' ? coinbaseColors.dark.background : coinbaseColors.white,
        paper: mode === 'dark' ? coinbaseColors.dark.surface : coinbaseColors.bgPrimary,
      },
      divider: mode === 'dark' ? coinbaseColors.mediumGray : coinbaseColors.borderGray,
    },
    typography: {
      fontFamily: "'Graphik', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
      h1: {
        fontWeight: 800,
      },
      h2: {
        fontWeight: 700,
      },
      button: {
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: mode === 'dark' ? coinbaseColors.dark.surface : coinbaseColors.white,
              '&:hover': {
                backgroundColor: mode === 'dark' ? coinbaseColors.mediumGray : coinbaseColors.white,
              },
              '&.Mui-focused': {
                backgroundColor: mode === 'dark' ? coinbaseColors.mediumGray : coinbaseColors.white,
              },
              '& fieldset': {
                borderColor: mode === 'dark' ? coinbaseColors.lightGray : coinbaseColors.borderGray,
              },
              '&:hover fieldset': {
                borderColor: mode === 'dark' ? coinbaseColors.textGray : coinbaseColors.subtleGray,
              },
              '&.Mui-focused fieldset': {
                borderColor: coinbaseColors.primary,
              },
            },
            '& .MuiInputLabel-root': {
              color: mode === 'dark' ? coinbaseColors.dark.muted : coinbaseColors.lightGray,
              '&.Mui-focused': {
                color: coinbaseColors.primary,
              },
            },
            '& .MuiInputBase-input': {
              color: mode === 'dark' ? coinbaseColors.white : coinbaseColors.darkGray,
              '&::placeholder': {
                color: mode === 'dark' ? coinbaseColors.textGray : coinbaseColors.textGray,
                opacity: 1,
              },
            },
            '& .MuiFormHelperText-root': {
              color: mode === 'dark' ? '#9CA3AF' : '#6B7280',
              '&.Mui-error': {
                color: mode === 'dark' ? '#F87171' : '#DC2626',
              },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            '& .MuiSelect-icon': {
              color: mode === 'dark' ? '#9CA3AF' : '#6B7280',
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? '#6B7280' : '#D1D5DB',
            '&.Mui-checked': {
              color: mode === 'dark' ? '#0066FF' : '#0066FF',
            },
            '&.Mui-disabled': {
              color: mode === 'dark' ? '#374151' : '#F3F4F6',
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            '& .MuiFormControlLabel-label': {
              color: mode === 'dark' ? '#F9FAFB' : '#1F2937',
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? '#9CA3AF' : '#6B7280',
            '&.Mui-focused': {
              color: mode === 'dark' ? '#0066FF' : '#0066FF',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? '#9CA3AF' : '#6B7280',
            '&.Mui-error': {
              color: mode === 'dark' ? '#F87171' : '#DC2626',
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? '#6B7280' : '#D1D5DB',
            '&.Mui-checked': {
              color: mode === 'dark' ? '#0066FF' : '#0066FF',
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-switchBase': {
              color: mode === 'dark' ? '#6B7280' : '#D1D5DB',
            },
            '& .MuiSwitch-track': {
              backgroundColor: mode === 'dark' ? '#4B5563' : '#E5E7EB',
            },
            '& .Mui-checked': {
              '& .MuiSwitch-switchBase': {
                color: mode === 'dark' ? '#0066FF' : '#0066FF',
              },
              '& .MuiSwitch-track': {
                backgroundColor: mode === 'dark' ? '#1E40AF' : '#BFDBFE',
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#374151' : '#F3F4F6',
            color: mode === 'dark' ? '#F9FAFB' : '#1F2937',
            '&.MuiChip-clickable:hover': {
              backgroundColor: mode === 'dark' ? '#4B5563' : '#E5E7EB',
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: mode === 'dark' ? '#374151' : '#FFFFFF',
              '&:hover': {
                backgroundColor: mode === 'dark' ? '#4B5563' : '#FFFFFF',
              },
              '&.Mui-focused': {
                backgroundColor: mode === 'dark' ? '#4B5563' : '#FFFFFF',
              },
            },
          },
          paper: {
            backgroundColor: mode === 'dark' ? '#1F2937' : '#FFFFFF',
            border: mode === 'dark' ? '1px solid #374151' : '1px solid #E5E7EB',
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#1F2937' : '#FFFFFF',
            border: mode === 'dark' ? '1px solid #374151' : '1px solid #E5E7EB',
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              backgroundColor: mode === 'dark' ? '#1F2937' : '#FFFFFF',
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#374151' : '#F9FAFB',
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#4B5563' : '#F3F4F6',
            },
            '&.Mui-expanded': {
              backgroundColor: mode === 'dark' ? '#374151' : '#F9FAFB',
            },
          },
          content: {
            color: mode === 'dark' ? '#F9FAFB' : '#1F2937',
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#1F2937' : '#FFFFFF',
            color: mode === 'dark' ? '#F9FAFB' : '#1F2937',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#1F2937' : '#FFFFFF',
            color: mode === 'dark' ? '#F9FAFB' : '#1F2937',
            border: mode === 'dark' ? '1px solid #374151' : '1px solid #E5E7EB',
          },
          standardInfo: {
            backgroundColor: mode === 'dark' ? '#1E3A8A' : '#DBEAFE',
            color: mode === 'dark' ? '#BFDBFE' : '#1E40AF',
            border: mode === 'dark' ? '1px solid #3B82F6' : '1px solid #93C5FD',
          },
          standardSuccess: {
            backgroundColor: mode === 'dark' ? '#065F46' : '#D1FAE5',
            color: mode === 'dark' ? '#A7F3D0' : '#047857',
            border: mode === 'dark' ? '1px solid #10B981' : '1px solid #6EE7B7',
          },
          standardWarning: {
            backgroundColor: mode === 'dark' ? '#92400E' : '#FEF3C7',
            color: mode === 'dark' ? '#FDE68A' : '#D97706',
            border: mode === 'dark' ? '1px solid #F59E0B' : '1px solid #FCD34D',
          },
          standardError: {
            backgroundColor: mode === 'dark' ? '#7F1D1D' : '#FEE2E2',
            color: mode === 'dark' ? '#FCA5A5' : '#DC2626',
            border: mode === 'dark' ? '1px solid #EF4444' : '1px solid #F87171',
          },
        },
      },

    },
  });
};

// Default light theme for backward compatibility
export const theme = createAppTheme('light'); 